<template>
  <div
    data-test="graphics-marqueeTextSettings"
  >
    <SidebarSettingsGroup
      v-if="!element.data?.noRemap && !editingMultiple"
      title="Content"
      collapsible
      :element-type="element.type"
      data-test="graphics-marqueeTextSettings-content"
    >
      <div class="sidebar-form-group">
        <div class="text-settings-header flex flex-center-all margin-bottom-sm">
          <p class="sidebar-form-label flex-grow">
            Text
          </p>
        </div>

        <p
          v-if="isMapped('text')"
          class="sidebar-form-label margin-bottom-sm"
          data-test="graphics-marqueeTextSettings-mappedText"
        >
          From Data Provider
        </p>

        <map-data-control
          v-if="dataProvider"
          :data-provider="dataProvider"
          :scroll-offset="scrollOffset"
          :element="element"
          property="text"
          :mapped="isMapped('text')"
          @update-map="mapToField"
          @clear-map="clearMapping"
        />

        <!-- Edit contents of text element, or bind to spreadsheet with vars -->
        <div
          v-if="!isMapped('text')"
          class="flex margin-bottom-sm"
        >
          <div
            v-once
            id="textContentField"
            ref="editor"
            class="contenteditable-editor"
            contenteditable
            spellcheck="false"
            @input="onChangeText($event, true)"
            @blur="onBlur"
            @focus="isEditingText = true"
            @keydown.stop
            v-html="text"
          />
        </div>
      </div>
    </SidebarSettingsGroup>

    <SidebarSettingsGroup
      title="Style"
      collapsible
      :element-type="element.type"
      data-test="graphics-marqueeTextSettings-style"
    >
      <div class="sidebar-form-group">
        <div class="text-setting-header">
          <label class="sidebar-form-label"> Font </label>
        </div>

        <font-dropdown
          :custom-fonts="customFontsArray"
          :element="element"
        />

        <div class="settings-row settings-row-xs">
          <label class="sidebar-form-label">Text Style</label>
          <div class="settings-col-4 settings-col-xs">
            <validation-field as="input"
              v-model.number="fontSize"
              rules="required|numeric|min:0"
              type="number"
              name="fontSize"
              min="0"
              :class="{ 'is-invalid': validationErrors['fontSize'] }"
              data-test="graphics-marqueeTextSettings-setFontSize"
              data-entity="graphics-marqueeTextSettings-setFontSize"
            />
            <label class="sidebar-input-sub-label">Size</label>
          </div>

          <div class="settings-col-4 settings-col-xs">
            <validation-field as="input"
              v-model.number="letterSpacing"
              rules="required|numeric|min:0"
              type="number"
              name="letterSpacing"
              min="0"
              :class="{ 'is-invalid': validationErrors['letterSpacing'] }"
              data-test="graphics-marqueeTextSettings-setLetterSpacing"
              data-entity="graphics-marqueeTextSettings-setLetterSpacing"
            />
            <label class="sidebar-input-sub-label">Letter spacing</label>
          </div>

          <div class="settings-col-4 settings-col-xs">
            <validation-field as="input"
              title="Line spacing does not effect non-wrapped marquee text."
              v-model.number="lineHeight"
              rules="required|decimal|min:0"
              type="number"
              name="lineHeight"
              min="0"
              step="0.1"
              :class="{ 'is-invalid': validationErrors['lineHeight'] }"
              :disabled="true /* marquee doesn't wrap for now so this doesn't do anything
              however I am leaving it in for balance and future use. */"
              data-test="graphics-marqueeTextSettings-setLineHeight"
              data-entity="graphics-marqueeTextSettings-setLineHeight"
            />
            <label class="sidebar-input-sub-label">Line Spacing</label>
          </div>
        </div>
        <div class="settings-row settings-row-xs">
          <div class="settings-col-12 settings-col-xs">
            <div class="colorpicker">
              <colorpicker
                v-model="color"
                :is-mapped="isMapped('color')"
              />
            </div>
            <label class="sidebar-input-sub-label">
              Color
              <span
                data-balloon="This setting is controlled by data. Unmap data to customize."
                data-balloon-pos="down"
                data-balloon-length="medium"
              >
                <span
                  v-if="isMapped('color')"
                  class="fa fa-lock mapped-lock"
                />
              </span>
            </label>
          </div>
        </div>
        <!-- TODO: remove once sheets can have more than one thing mapped -->
        <template v-if="dataProvider && dataProvider?.type !== 'sheet-data'">
          <p
            v-if="isTextStyleMapped"
            class="sidebar-form-label margin-bottom-sm"
          >
            From Data Provider
          </p>
          <map-data-control
            :data-provider="dataProvider"
            :scroll-offset="scrollOffset"
            :element="element"
            property="color"
            :mapped="isMapped('color')"
            @update-map="mapToField"
            @clear-map="clearMapping"
          />
        </template>

        <div class="settings-row settings-row-xs text-styles">
          <div class="settings-col-12 settings-col-xs transforms">
            <button
              aria-label="Bold"
              class="btn btn-sm btn-icon"
              :class="getFontWeightBtnClass"
              data-test="graphics-marqueeTextSettings-bold"
              data-entity="graphics-marqueeTextSettings-bold"
              @click="setFontWeight"
            >
              <span class="fa-solid fa-bold" />
            </button>
            <button
              aria-label="Italic"
              class="btn btn-sm btn-icon"
              :class="getFontStyleBtnClass"
              data-test="graphics-marqueeTextSettings-italic"
              data-entity="graphics-marqueeTextSettings-italic"
              @click="setFontStyle"
            >
              <span class="fa-solid fa-italic" />
            </button>
            <button
              aria-label="Underline"
              class="btn btn-sm btn-icon"
              :class="getUnderlineBtnClass"
              data-test="graphics-marqueeTextSettings-underline"
              data-entity="graphics-marqueeTextSettings-underline"
              @click="setUnderlineStyle"
            >
              <span class="fa-solid fa-underline" />
            </button>
            <button
              aria-label="Strikethrough"
              class="btn btn-sm btn-icon"
              :class="getStrikethroughBtnClass"
              data-test="graphics-marqueeTextSettings-strikethrough"
              data-entity="graphics-marqueeTextSettings-strikethrough"
              @click="setStrikethroughStyle"
            >
              <span class="fa-solid fa-strikethrough" />
            </button>
            <button
              aria-label="Uppercase"
              class="btn btn-sm btn-icon"
              :class="getTransformBtnClass"
              data-test="graphics-marqueeTextSettings-uppercase"
              data-entity="graphics-marqueeTextSettings-uppercase"
              @click="setTransformStyle"
            >
              <span class="fa-solid fa-font" />
            </button>
          </div>
        </div>
      </div>
      <hr class="sidebar-divider">

      <div class="sidebar-form-group">
        <label class="checkbox header-checkbox">Text shadow
          <input
            v-model="textShadow"
            type="checkbox"
            data-test="graphics-marqueeTextSettings-toggleTextShadow"
            data-entity="graphics-marqueeTextSettings-toggleTextShadow"
          />
          <div class="checkmark">
            <span class="checkmark-tick fa-solid fa-check" />
          </div>
        </label>

        <div
          v-if="textShadow"
          class="settings-row settings-row-xs"
        >
          <div class="settings-col-4 settings-col-xs">
            <validation-field as="input"
              v-model.number="textShadowX"
              rules="required|integer"
              type="number"
              name="textShadowX"
              :class="{ 'is-invalid': validationErrors['textShadowX'] }"
              data-test="graphics-marqueeTextSettings-setTextShadowX"
              data-entity="graphics-marqueeTextSettings-setTextShadowX"
            />
            <label class="sidebar-input-sub-label">X</label>
          </div>
          <div class="settings-col-4 settings-col-xs">
            <validation-field as="input"
              v-model.number="textShadowY"
              rules="required|integer"
              type="number"
              name="textShadowY"
              :class="{ 'is-invalid': validationErrors['textShadowY'] }"
              data-test="graphics-marqueeTextSettings-setTextShadowY"
              data-entity="graphics-marqueeTextSettings-setTextShadowY"
            />
            <label class="sidebar-input-sub-label">Y</label>
          </div>
          <div class="settings-col-4 settings-col-xs">
            <validation-field as="input"
              v-model.number="textShadowBlur"
              rules="required|integer"
              type="number"
              name="textShadowBlur"
              :class="{ 'is-invalid': validationErrors['textShadowBlur'] }"
              data-test="graphics-marqueeTextSettings-setTextShadowBlur"
              data-entity="graphics-marqueeTextSettings-setTextShadowBlur"
            />
            <label class="sidebar-input-sub-label">Blur</label>
          </div>
          <div class="settings-col settings-col-xs colorpicker margin-bottom-sm">
            <colorpicker
              v-model="textShadowColor"
              :is-mapped="isMapped('textShadowColor')"
            />
            <label class="sidebar-input-sub-label">
              Color
              <span
                data-balloon="This setting is controlled by data. Unmap data to customize."
                data-balloon-pos="down"
                data-balloon-length="medium"
              >
                <span
                  v-if="isMapped('textShadowColor')"
                  class="fa fa-lock mapped-lock"
                />
              </span>
            </label>
          </div>

          <!-- TODO: remove once sheets can have more than one thing mapped -->
          <template v-if="dataProvider && dataProvider?.type !== 'sheet-data'">
            <p
              v-if="isMapped('textShadowColor')"
              class="sidebar-form-label margin-bottom-sm"
            >
              From Data Provider
            </p>
            <map-data-control
              :data-provider="dataProvider"
              :scroll-offset="scrollOffset"
              :element="element"
              property="textShadowColor"
              :mapped="isMapped('textShadowColor')"
              @update-map="mapToField"
              @clear-map="clearMapping"
            />
          </template>
        </div>
      </div>

      <hr class="sidebar-divider">

      <div class="sidebar-form-group">
        <label class="checkbox header-checkbox">Background color
          <input
            v-model="background"
            type="checkbox"
            data-test="graphics-marqueeTextSettings-toggleBackground"
            data-entity="graphics-marqueeTextSettings-toggleBackground"
          >
          <div class="checkmark">
            <span class="checkmark-tick fa-solid fa-check" />
          </div>
        </label>

        <div
          v-if="background"
          class="settings-row settings-row-xs"
        >
          <label
            class="radio inline-radio"
            for="solid"
          >Solid
            <input
              id="solid"
              v-model="fillStyle"
              type="radio"
              value="solid"
              data-test="graphics-marqueeTextSettings-fillStyleSolid"
              data-entity="graphics-marqueeTextSettings-fillStyleSolid"
            >
            <div class="checkmark" />
          </label>
          <label
            class="radio inline-radio"
            for="gradient"
          >Gradient
            <input
              id="gradient"
              v-model="fillStyle"
              type="radio"
              value="gradient"
              data-test="graphics-marqueeTextSettings-fillStyleGradient"
              data-entity="graphics-marqueeTextSettings-fillStyleGradient"
            >
            <div class="checkmark" />
          </label>

          <div
            v-if="fillStyle == 'solid'"
            class="colorpicker text-center"
          >
            <colorpicker
              v-model="backgroundColor"
              :is-mapped="isMapped('backgroundColor')"
            />
            <span
              data-balloon="This setting is controlled by data. Unmap data to customize."
              data-balloon-pos="down"
              data-balloon-length="medium"
            >
              <span
                v-if="isMapped('backgroundColor')"
                class="fa fa-lock mapped-lock"
              />
            </span>
          </div>

          <div
            v-else
            class="settings-row settings-row-xs"
          >
            <div class="settings-col-6 settings-col-xs">
              <div class="input-group">
                <validation-field as="input"
                  v-model.number="gradientDirection"
                  rules="required|numeric"
                  type="number"
                  name="gradientDirection"
                  :class="{ 'is-invalid': validationErrors['gradientDirection'] }"
                  data-test="graphics-marqueeTextSettings-setGradientDirection"
                  data-entity="graphics-marqueeTextSettings-setGradientDirection"
                />
                <span class="input-group-addon symbol">°</span>
              </div>
            </div>
            <div class="settings-col-3 settings-col-xs colorpicker text-center">
              <colorpicker
                v-model="gradientColorOne"
                :is-mapped="isMapped('gradientColorOne')"
              />
              <span
                data-balloon="This setting is controlled by data. Unmap data to customize."
                data-balloon-pos="down"
                data-balloon-length="medium"
              >
                <span
                  v-if="isMapped('gradientColorOne')"
                  class="fa fa-lock mapped-lock"
                />
              </span>
            </div>
            <div class="settings-col-3 settings-col-xs colorpicker text-center">
              <colorpicker
                v-model="gradientColorTwo"
                :is-mapped="isMapped('gradientColorTwo')"
              />
              <span
                data-balloon="This setting is controlled by data. Unmap data to customize."
                data-balloon-pos="down-right"
                data-balloon-length="medium"
              >
                <span
                  v-if="isMapped('gradientColorTwo')"
                  class="fa fa-lock mapped-lock"
                />
              </span>
            </div>
          </div>

          <!-- TODO: remove once sheets can have more than one thing mapped -->
          <div
            v-if="dataProvider && dataProvider?.type !== 'sheet-data'"
            class="margin-top-sm"
          >
            <p
              v-if="isBackgroundColorMapped"
              class="sidebar-form-label margin-top-sm"
              data-test="graphics-marqueeTextSettings-mappedBackgroundColor"
            >
              From Data Provider
            </p>

            <map-data-control
              v-if="fillStyle == 'solid'"
              :data-provider="dataProvider"
              :element="element"
              :scroll-offset="scrollOffset"
              property="backgroundColor"
              :mapped="isMapped('backgroundColor')"
              @update-map="mapToField"
              @clear-map="clearMapping"
            />
            <map-data-control
              v-if="fillStyle == 'gradient'"
              :data-provider="dataProvider"
              :element="element"
              :scroll-offset="scrollOffset"
              property="gradientColorOne"
              :mapped="isMapped('gradientColorOne')"
              @update-map="mapToField"
              @clear-map="clearMapping"
            />
            <map-data-control
              v-if="fillStyle == 'gradient'"
              :data-provider="dataProvider"
              :element="element"
              :scroll-offset="scrollOffset"
              property="gradientColorTwo"
              :mapped="isMapped('gradientColorTwo')"
              @update-map="mapToField"
              @clear-map="clearMapping"
            />
          </div>
        </div>
      </div>
    </SidebarSettingsGroup>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import EventBus from '../../../utils/event-bus';
import SettingsMixins from '../../../mixins/settings-mixins';

// import AnimationSettings from '../Animation.vue';
import MapDataControl from '../Data/MapDataControl.vue';
import FontDropdown from '../FontDropdown.vue';

export default {
  name: 'MarqueeTextSettings',
  components: {
    // AnimationSettings,
    FontDropdown,
    MapDataControl,
  },
  mixins: [SettingsMixins],
  props: ['element', 'scrollOffset'],

  data() {
    return {
      isEditingText: false,

      alignmentTooltips: {
        left: 'Left',
        center: 'Center',
        right: 'Right',
        justify: 'Justify',
      },

      alignments: ['left', 'center', 'right', 'justify'],
      verticalAlignments: ['top', 'middle', 'bottom'],

      textBox: [
        {
          icon: 'fak fa-fixed-height-width',
          tooltip: 'Fixed size textbox',
          id: 1,
        },
        { icon: 'fak fa-auto-height-width', tooltip: 'Autosize textbox', id: 2 },
      ],

      textFlow: [
        { icon: 'fak fa-no-wrap-text', tooltip: 'No wrap', id: 1 },
        { icon: 'fak fa-wrap-text', tooltip: 'Wrap text', id: 2 },
      ],
    };
  },

  computed: {
    ...mapGetters(['customFontsArray', 'customFonts']),
    text: {
      get() {
        return this.element.text || 'Edit the text in the right side bar.';
      },
      set(v) {
        this.updateOneElementValue('text', v);
      },
    },
    fontSize: {
      get() {
        return this.element.styles.fontSize;
      },
      set(v) {
        this.updateOneElementValue('styles.fontSize', v);
      },
    },
    letterSpacing: {
      get() {
        return this.element.styles.letterSpacing;
      },
      set(v) {
        this.updateOneElementValue('styles.letterSpacing', v);
      },
    },
    lineHeight: {
      get() {
        return this.element.styles.lineHeight ?? 1.25; // preserve old text elements
      },
      set(v) {
        this.updateOneElementValue('styles.lineHeight', v);
      },
    },
    color: {
      get() {
        return (this.isMapped('color') && this.dataValue('color')) || this.element.styles.color;
      },
      set(v) {
        this.updateOneElementValue('styles.color', v);
      },
    },
    textShadow: {
      get() {
        return this.element.textShadow;
      },
      set(v) {
        this.updateOneElementValue('textShadow', v);
      },
    },
    textShadowX: {
      get() {
        return this.element.textShadowX;
      },
      set(v) {
        this.updateOneElementValue('textShadowX', v);
      },
    },
    textShadowY: {
      get() {
        return this.element.textShadowY;
      },
      set(v) {
        this.updateOneElementValue('textShadowY', v);
      },
    },
    textShadowBlur: {
      get() {
        return this.element.textShadowBlur;
      },
      set(v) {
        this.updateOneElementValue('textShadowBlur', v);
      },
    },
    textShadowColor: {
      get() {
        return (this.isMapped('textShadowColor') && this.dataValue('textShadowColor')) || this.element.textShadowColor;
      },
      set(v) {
        this.updateOneElementValue('textShadowColor', v);
      },
    },
    background: {
      get() {
        return this.element.background;
      },
      set(v) {
        this.updateOneElementValue('background', v);
      },
    },
    fillStyle: {
      get() {
        return this.element.fillStyle;
      },
      set(v) {
        this.updateOneElementValue('fillStyle', v);
      },
    },
    backgroundColor: {
      get() {
        return (this.isMapped('backgroundColor') && this.dataValue('backgroundColor')) || this.element.backgroundColor;
      },
      set(v) {
        this.updateOneElementValue('backgroundColor', v);
      },
    },
    gradientDirection: {
      get() {
        return this.element.gradientDirection;
      },
      set(v) {
        this.updateOneElementValue('gradientDirection', v);
      },
    },
    gradientColorOne: {
      get() {
        return (this.isMapped('gradientColorOne') && this.dataValue('gradientColorOne')) || this.element.gradientColorOne;
      },
      set(v) {
        this.updateOneElementValue('gradientColorOne', v);
      },
    },
    gradientColorTwo: {
      get() {
        return (this.isMapped('gradientColorTwo') && this.dataValue('gradientColorTwo')) || this.element.gradientColorTwo;
      },
      set(v) {
        this.updateOneElementValue('gradientColorTwo', v);
      },
    },

    getFontName() {
      return this.fonts.filter(
        (font) => font.font === this.element.styles.fontFamily,
      )[0];
    },

    getFontWeightBtnClass() {
      return this.element.styles.fontWeight === 'bold'
        ? 'btn-style-a'
        : 'btn-style-d';
    },

    getFontStyleBtnClass() {
      return this.element.styles.fontStyle === 'italic'
        ? 'btn-style-a'
        : 'btn-style-d';
    },

    getUnderlineBtnClass() {
      if (this.element.styles.textDecoration === 'underline') {
        return 'btn-style-a';
      }
      return 'btn-style-d';
    },

    getStrikethroughBtnClass() {
      if (this.element.styles.textDecoration === 'line-through') {
        return 'btn-style-a';
      }
      return 'btn-style-d';
    },

    getTransformBtnClass() {
      return this.element.styles.textTransform === 'uppercase'
        ? 'btn-style-a'
        : 'btn-style-d';
    },

    plan() {
      return this.$store.state.user.plan;
    },

    fontLabelTooltip() {
      return this.plan.permissions.custom_fonts
        ? 'Font Uploader is here! Now you can upload your very own fonts. Click the dropdown below to get started.'
        : 'Font Uploader is here! This feature requires a Premium or Ultimate package.';
    },

    isTextStyleMapped() {
      return this.isMapped('color');
    },
  },

  watch: {
    text(val) {
      if (!this.isEditingText) {
        if (this.$refs?.editor) {
          this.$refs.editor.innerHTML = val;
        }
      }
    },
  },

  beforeUnmount() {
    // Just in case change hasn't been saved, save now
    if (!this.dataProvider) {
      clearTimeout(this.textChangeTimeout);
    }
  },

  methods: {
    onClick(e) {
      // close dropdown if click outside
      const { dataProviderDropdown } = this.$refs;
      if (
        dataProviderDropdown
        && !dataProviderDropdown.$el.contains(e.target)
      ) {
        this.showInjectDropdown = false;
      }
    },

    onChangeText(e, debounce) {
      clearTimeout(this.textChangeTimeout);
      this.textChangeTimeout = setTimeout(
        () => {
          const text = this.sanitizeHtml(e.target.innerHTML);
          this.text = text;
        },
        debounce ? 500 : 0,
      );
    },

    sanitizeHtml(html) {
      const div = document.createElement('div');
      div.innerHTML = html;
      div.querySelectorAll('span[style]').forEach((span) => {
        span.replaceWith(...span.childNodes);
      });
      return div.innerHTML;
    },

    onBlur(e) {
      this.isEditingText = false;
      clearTimeout(this.textChangeTimeout);
      this.text = this.sanitizeHtml(e.target.innerHTML);
    },

    setFontWeight() {
      if (this.element.styles.fontWeight === 'normal') {
        this.updateOneElementValue('styles.fontWeight', 'bold');
      } else {
        this.updateOneElementValue('styles.fontWeight', 'normal');
      }
      EventBus.emit('text-adjusted');
    },

    setFontStyle() {
      if (this.element.styles.fontStyle === 'normal') {
        this.updateOneElementValue('styles.fontStyle', 'italic');
      } else {
        this.updateOneElementValue('styles.fontStyle', 'normal');
      }
      EventBus.emit('text-adjusted');
    },

    setUnderlineStyle() {
      if (
        this.element.styles.textDecoration === 'none'
        || this.element.styles.textDecoration === 'line-through'
      ) {
        this.updateOneElementValue('styles.textDecoration', 'underline');
      } else {
        this.updateOneElementValue('styles.textDecoration', 'none');
      }
      EventBus.emit('text-adjusted');
    },

    setStrikethroughStyle() {
      if (
        this.element.styles.textDecoration === 'none'
        || this.element.styles.textDecoration === 'underline'
      ) {
        this.updateOneElementValue('styles.textDecoration', 'line-through');
      } else {
        this.updateOneElementValue('styles.textDecoration', 'none');
      }
      EventBus.emit('text-adjusted');
    },

    setTransformStyle() {
      if (this.element.styles.textTransform === 'none') {
        this.updateOneElementValue('styles.textTransform', 'uppercase');
      } else {
        this.updateOneElementValue('styles.textTransform', 'none');
      }
      EventBus.emit('text-adjusted');
    },

    setFont(font) {
      this.updateOneElementValue('styles.fontFamily', font);
      EventBus.emit('text-adjusted');
    },
  },
};
</script>
